import React, { forwardRef } from 'react';
import { AxiosPromise } from 'axios';
import {
  ColDef, ColGroupDef, SelectionChangedEvent
} from '@ag-grid-enterprise/all-modules';
import CommonGrid from 'shared/commonGrid';
import { ROWSELECTION } from 'shared/commonGrid/grid-enums';
import { FilterModel } from 'shared/commonGrid/common-grid.types';
import { EWarrantGridRowData } from 'data/eWarrant/eWarrant.types';
import { GridSettings, ServerResponse, UserSettings } from 'data/types';
import isTestEnvironment from 'utils/environment/isTestEnvironment';
import EWarrantStatusCellRenderer from 'routes/EWarrant/components/EWarrantStatusCellRenderer';
import EWarrantListMenu from '../EWarrantListMenu';
import './e-warrant-grid.scss';

type Props = {
  selectedEWarrant?: EWarrantGridRowData
  columnDefs: (ColGroupDef | ColDef)[]
  columnDefsVersion: string
  noResults: boolean
  beforeLogout: boolean
  lastUpdatedAt: Date | undefined
  hasPrivilegeToView: boolean
  suppressColumnVirtualisation?: boolean
  onRefresh: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  onSelectionChanged: (event: SelectionChangedEvent) => void
  onSaveGridSettings: (gridSettings: GridSettings) => void
  onGetGridSettings: () => {
    type: string
    payload: AxiosPromise<ServerResponse<UserSettings>>
  }
  /** Runs the list read for the paging, sorting and filtering the grid asks for. */
  onGetResultsFromServer: (query: string) => unknown
  onAdaptToFilterModel: (filterModel: FilterModel) => FilterModel
  onView: () => void
};

/**
 * The e-warrant list grid. Its rows are read from the server rather than held in the
 * store, so the paging, sorting and column filters the operator sets all travel to the
 * backend — the ref is forwarded to CommonGrid for the same reason the warrants grid keeps
 * one, so the container can make the grid re-read after it has changed an e-warrant.
 */
const EWarrantGrid = forwardRef<CommonGrid, Props>(({
  selectedEWarrant,
  columnDefs,
  columnDefsVersion,
  noResults,
  beforeLogout,
  lastUpdatedAt,
  hasPrivilegeToView,
  suppressColumnVirtualisation = isTestEnvironment(),
  onRefresh,
  onSelectionChanged,
  onSaveGridSettings,
  onGetGridSettings,
  onGetResultsFromServer,
  onAdaptToFilterModel,
  onView,
}: Props, ref) => {
  const frameworkComponents = {
    eWarrantStatusCellRenderer: EWarrantStatusCellRenderer,
  };

  return (
    <div className="grid-container e-warrant-grid" style={{ height: 'inherit' }}>
      <CommonGrid
        ref={ref}
        pagination
        enableBrowserTooltips
        renderDataFromServer
        rowSelection={ROWSELECTION.SINGLE}
        // Ignored while renderDataFromServer is on — the rows come from the datasource.
        rowData={[]}
        noResults={noResults}
        isBeforeLogout={beforeLogout}
        columnDefs={columnDefs}
        columnDefsVersion={columnDefsVersion}
        components={frameworkComponents}
        suppressColumnVirtualisation={suppressColumnVirtualisation}
        lastUpdatedAt={lastUpdatedAt}
        onRefresh={onRefresh}
        onSelectionChanged={onSelectionChanged}
        saveGridSettings={onSaveGridSettings}
        getGridSettingsFromServer={onGetGridSettings}
        getGridResultsFromServer={onGetResultsFromServer}
        adaptToFilterModel={onAdaptToFilterModel}
        onActionMenuItemClick={onView}
      >
        <EWarrantListMenu
          selectedEWarrant={selectedEWarrant}
          hasPrivilegeToView={hasPrivilegeToView}
          onView={onView}
        />
      </CommonGrid>
    </div>
  );
});

export default EWarrantGrid;
